'use client';

import { useEffect, useRef, useState } from 'react';

import { BarChart3, GraduationCap, Plug } from 'lucide-react';

import { AppLogo } from '@kit/ui/patterns/app-logo';

import { ConversionFunnel } from '../../home/[account]/analytics/_components/conversion-funnel';
import { EnrolmentSummaryCards } from '../../home/[account]/analytics/_components/enrolment-summary-cards';
import { RevenueWidget } from '../../home/[account]/analytics/_components/revenue-widget';
import { WaitlistReport } from '../../home/[account]/analytics/_components/waitlist-report';
import styles from './axcelerate-analytics-demo.module.css';

const chapters = [
  [
    'Follow every enrolment',
    'See attempts, successful enrolments, abandoned journeys and invoiced revenue together. Your team can see what needs a closer look.',
  ],
  [
    'Find where people drop off',
    'Follow Started → Enrolled → Payment Completed, with a separate verification branch. Compare the stages to see where students need more support.',
  ],
  [
    'Understand the payment picture',
    'Compare invoiced amounts with paid enrolments and those still awaiting payment. Keep the difference between enrolment and payment clear.',
  ],
  [
    'See demand beyond available places',
    'Review waitlisted students and course demand to help your team plan its next step.',
  ],
];
// Local presentation records. The real, fetch-free app UI components below receive these props.
const metrics = {
  totalStarted: 40,
  totalCreated: 30,
  totalConfirmed: 24,
  totalFailed: 8,
  totalWaitlisted: 2,
  totalPayments: 24,
  revenueInvoiced: 7200,
};
const stages = [
  { stage: 'started', label: 'Started', eventCount: 40 },
  { stage: 'enrolled', label: 'Enrolled', eventCount: 30 },
  { stage: 'payment_completed', label: 'Payment Completed', eventCount: 24 },
  { stage: 'otp_sent', label: 'OTP Sent', eventCount: 20 },
  { stage: 'otp_verified', label: 'OTP Verified', eventCount: 18 },
].map((stage, index) => ({
  ...stage,
  stageOrder: index,
  dropOffPercent: null,
}));

export function AxcelerateAnalyticsDemo({
  previewTime,
}: {
  previewTime?: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [clock, setClock] = useState(0);
  const time = previewTime ?? clock;
  const chapter = Math.floor(time / 8000) % 4;
  useEffect(() => {
    if (previewTime !== undefined) return;
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let last = performance.now();
    const observer = new IntersectionObserver(([entry]) => {
      visible = !!entry?.isIntersecting;
      last = performance.now();
    });
    if (root.current) observer.observe(root.current);
    const timer = window.setInterval(() => {
      const now = performance.now();
      if (visible && !document.hidden && !media.matches)
        setClock((value) => (value + Math.min(now - last, 250)) % 32000);
      last = now;
    }, 100);
    return () => {
      observer.disconnect();
      clearInterval(timer);
    };
  }, [previewTime]);
  return (
    <div ref={root} className={styles.scene} data-ax-analytics>
      <div className={styles.dashboard}>
        <header className={styles.header}>
          <AppLogo className="w-28" />
          <span>Analytics · This month</span>
        </header>
        <div className={styles.tabs}>
          <span>
            <BarChart3 size={15} />
            Overview
          </span>
          <span data-selected>
            <GraduationCap size={15} />
            Enrolments
          </span>
          <span>
            <Plug size={15} />
            Integrations
          </span>
        </div>
        <div className={styles.summary} data-focus={chapter === 0}>
          <EnrolmentSummaryCards metrics={metrics} />
        </div>
        <div
          className={styles.detail}
          data-focus={chapter > 0}
          data-funnel={chapter < 2}
          key={chapter}
        >
          {chapter < 2 ? (
            <ConversionFunnel stages={stages} />
          ) : chapter === 2 ? (
            <RevenueWidget metrics={metrics} />
          ) : (
            <WaitlistReport
              waitlist={{
                totalWaitlisted: 2,
                courses: [
                  {
                    courseName: 'First Aid',
                    courseInstanceId: 'first-aid',
                    waitlistCount: 2,
                    avgWaitHours: 12,
                  },
                ],
              }}
            />
          )}
        </div>
      </div>
      <div className={styles.annotation}>
        <small>0{chapter + 1} / ANALYTICS</small>
        <h3>{chapters[chapter]![0]}</h3>
        <p>{chapters[chapter]![1]}</p>
        <div className={styles.steps} aria-label="Analytics chapters">
          {chapters.map(([title], index) => (
            <button
              type="button"
              key={title}
              aria-label={title}
              aria-pressed={chapter === index}
              onClick={() => setClock(index * 8000)}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
